<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  // Current search value shown in the input
  searchValue: { type: String, default: '' },
  // v-model for selected audience label ids
  modelValue: { type: Array, default: () => [] },
  // v-model:inboxes for selected inbox ids
  inboxes: { type: Array, default: () => [] },
  // v-model:statuses for selected campaign statuses
  statuses: { type: Array, default: () => [] },
  // v-model:operator for combining audience + inbox filters
  operator: {
    type: String,
    default: 'and',
    validator: v => ['and', 'or'].includes(v),
  },
});

const emit = defineEmits([
  'search',
  'update:modelValue',
  'update:inboxes',
  'update:statuses',
  'update:operator',
]);

const { t } = useI18n();

const labels = useMapGetter('labels/getLabels');
const allInboxes = useMapGetter('inboxes/getInboxes');
const showFilter = ref(false);

const selectedLabels = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});

const selectedInboxes = computed({
  get: () => props.inboxes,
  set: v => emit('update:inboxes', v),
});

const selectedStatuses = computed({
  get: () => props.statuses,
  set: v => emit('update:statuses', v),
});

const selectedOperator = computed({
  get: () => props.operator,
  set: v => emit('update:operator', v),
});

const labelOptions = computed(() =>
  (labels.value || []).map(l => ({ value: l.id, label: l.title }))
);

const inboxOptions = computed(() =>
  (allInboxes.value || []).map(i => ({ value: i.id, label: i.name }))
);

// Supported statuses from Campaign model
// active, completed, error, running
const statusOptions = computed(() => [
  { value: 'active', label: t('CAMPAIGNS.FILTER.STATUS.OPTIONS.ACTIVE') },
  { value: 'completed', label: t('CAMPAIGNS.FILTER.STATUS.OPTIONS.COMPLETED') },
  { value: 'running', label: t('CAMPAIGNS.FILTER.STATUS.OPTIONS.RUNNING') },
  { value: 'error', label: t('CAMPAIGNS.FILTER.STATUS.OPTIONS.ERROR') },
]);

const hasActiveFilters = computed(
  () =>
    (selectedLabels.value?.length || 0) > 0 ||
    (selectedInboxes.value?.length || 0) > 0 ||
    (selectedStatuses.value?.length || 0) > 0
);

const onSearchInput = e => emit('search', e?.target?.value ?? '');
</script>

<template>
  <div class="w-full mx-auto max-w-[60rem]">
    <div
      class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-4 py-2"
    >
      <div class="flex items-center gap-2 w-full">
        <Input
          :model-value="searchValue"
          type="search"
          :placeholder="$t('CAMPAIGNS.HEADER.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="w-full"
          @input="onSearchInput"
        >
          <template #prefix>
            <span
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2 i-lucide-search"
            />
          </template>
        </Input>
        <div class="relative">
          <Button
            id="toggleCampaignsFilterButton"
            icon="i-lucide-list-filter"
            color="slate"
            size="sm"
            class="relative w-8"
            variant="ghost"
            @click="showFilter = !showFilter"
          >
            <div
              v-if="hasActiveFilters"
              class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
            />
          </Button>
          <div
            v-if="showFilter"
            class="fixed inset-x-4 top-24 sm:absolute sm:inset-auto sm:top-full sm:mt-1 sm:ltr:right-0 sm:rtl:left-0 z-40"
          >
            <OnClickOutside @trigger="showFilter = false">
              <div
                class="w-[calc(100vw-2rem)] sm:w-96 max-w-[calc(100vw-2rem)] border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-4 grid gap-3"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGNS.FILTER.TITLE') }}
                </span>
                <div class="grid gap-1">
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAMPAIGNS.FILTER.AUDIENCE.TITLE') }}
                  </span>
                  <TagMultiSelectComboBox
                    v-model="selectedLabels"
                    :options="labelOptions"
                    :search-placeholder="
                      t('CAMPAIGNS.FILTER.AUDIENCE.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="grid gap-1">
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAMPAIGNS.FILTER.INBOX.TITLE') }}
                  </span>
                  <TagMultiSelectComboBox
                    v-model="selectedInboxes"
                    :options="inboxOptions"
                    :search-placeholder="
                      t('CAMPAIGNS.FILTER.INBOX.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="grid gap-1">
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAMPAIGNS.FILTER.STATUS.TITLE') }}
                  </span>
                  <TagMultiSelectComboBox
                    v-model="selectedStatuses"
                    :options="statusOptions"
                    :search-placeholder="
                      t('CAMPAIGNS.FILTER.STATUS.PLACEHOLDER')
                    "
                  />
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAMPAIGNS.FILTER.OPERATOR.LABEL') }}
                  </span>
                  <div class="flex items-center gap-1">
                    <Button
                      sm
                      :variant="selectedOperator === 'and' ? 'solid' : 'ghost'"
                      color="slate"
                      @click="selectedOperator = 'and'"
                    >
                      {{ t('CAMPAIGNS.FILTER.OPERATOR.AND') }}
                    </Button>
                    <Button
                      sm
                      :variant="selectedOperator === 'or' ? 'solid' : 'ghost'"
                      color="slate"
                      @click="selectedOperator = 'or'"
                    >
                      {{ t('CAMPAIGNS.FILTER.OPERATOR.OR') }}
                    </Button>
                  </div>
                </div>
                <div class="flex justify-end gap-2">
                  <Button
                    sm
                    faded
                    slate
                    @click="
                      selectedLabels = [];
                      selectedInboxes = [];
                      selectedStatuses = [];
                      showFilter = false;
                    "
                  >
                    {{ t('CAMPAIGNS.FILTER.BUTTONS.CLEAR_FILTERS') }}
                  </Button>
                  <Button sm solid blue @click="showFilter = false">
                    {{ t('CAMPAIGNS.FILTER.BUTTONS.APPLY_FILTERS') }}
                  </Button>
                </div>
              </div>
            </OnClickOutside>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
